import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from "react";
import { Badge, Button, Sheet, SheetContent, SheetTitle, cn } from "@ndd/ui";
import type { useAuth } from "@clerk/react";
import type {
  AppointmentDetailResponse,
  AppointmentNoteResponse,
} from "../../../../shared/api-types";
import { FEATURE_FLAGS } from "../../../../shared/features";
import {
  formatAppointmentTime,
  shortDateLabel,
  type AppointmentListSearch,
} from "@/lib/appointments";
import {
  RAIL_TRANSITION,
  useIsDesktop,
  useShellGeometry,
  useShellSiderailSqueeze,
} from "@/components/v2/shell-layout";
import {
  chipView,
  panelView,
  type SessionRecapChipView,
  type SessionRecapLane,
} from "@/lib/session-recap-state";
import { useFeatureEnabled } from "@/lib/use-features";
import { initials } from "@/lib/avatar";
import { AppointmentNoteReadOnly } from "./appointment-note-read-only";
import { PatientQuickNote } from "./patient-quick-note";
import {
  AppointmentWorkspace,
  ReadOnlyAppointmentWorkspace,
} from "./appointment-workspace";
import { useAppointmentPresenceHeartbeat } from "./use-appointment-presence";
import { PreviousSoapNotesPanel, PreviousSoapNotesToggle } from "./previous-soap-notes";
import { previousNotesSheetState } from "./previous-soap-notes-sheet";
import { usePreviousSoapNotes } from "./use-previous-soap-notes";
import {
  WIDEN_MIN_VIEWPORT,
  initialOpenPanel,
  openPanelForBreakpoint,
  previousNotesDrawerWidth,
  reconcileOpenPanel,
  shellSqueezed,
  toggleOpenPanel,
  widenAvailable,
  type OpenPanel,
} from "./appointment-side-panel";
import { SessionRecapChip, SessionRecapPanel } from "./session-recap";
import { useSessionRecap, type SessionRecapHandle } from "./use-session-recap";
import { VisitLine } from "./appointment-visit-line";
import {
  CLASSIC_BACK_SLOT,
  CLASSIC_BREADCRUMB,
  HOME_LINK,
  NAME_AVATAR,
  NAME_ROW,
  NAME_TITLE,
} from "./workspace-look";

// The Previous notes drawer may be widened from this width up.
const PREVIOUS_NOTES_WIDE_QUERY = `(min-width: ${WIDEN_MIN_VIEWPORT}px)`;

function subscribeToPreviousNotesWide(onChange: () => void): () => void {
  const media = window.matchMedia(PREVIOUS_NOTES_WIDE_QUERY);
  media.addEventListener("change", onChange);

  return () => media.removeEventListener("change", onChange);
}

function useIsPreviousNotesWideViewport(): boolean {
  return useSyncExternalStore(
    subscribeToPreviousNotesWide,
    () => window.matchMedia(PREVIOUS_NOTES_WIDE_QUERY).matches
  );
}

export function AppointmentDetail({
  item,
  listSearch,
  timezone,
  note,
  readOnly,
  getToken,
}: {
  item: AppointmentDetailResponse;
  listSearch: AppointmentListSearch;
  timezone: string;
  note: AppointmentNoteResponse;
  /** An admin viewing another RD's Appointment: absence is the signal. */
  readOnly: boolean;
  getToken: ReturnType<typeof useAuth>["getToken"];
}): ReactElement {
  if (readOnly) {
    if (!listSearch.rdId) {
      return (
        <section aria-label="Appointment workspace" className="flex h-full min-h-0">
          <CanvasColumn>
            <AppointmentHeader
              item={item}
              listSearch={listSearch}
              timezone={timezone}
              readOnly
            />
            <AppointmentNoteReadOnly key={item.id} document={note.document} />
          </CanvasColumn>
        </section>
      );
    }

    return (
      <ReadOnlyAppointmentDetail
        item={item}
        listSearch={listSearch}
        timezone={timezone}
        note={note}
        rdId={listSearch.rdId}
        getToken={getToken}
      />
    );
  }

  return (
    <EditableAppointmentDetail
      item={item}
      listSearch={listSearch}
      timezone={timezone}
      note={note}
      getToken={getToken}
    />
  );
}

function ReadOnlyAppointmentDetail({
  item,
  listSearch,
  timezone,
  note,
  rdId,
  getToken,
}: {
  item: AppointmentDetailResponse;
  listSearch: AppointmentListSearch;
  timezone: string;
  note: AppointmentNoteResponse;
  rdId: string;
  getToken: ReturnType<typeof useAuth>["getToken"];
}): ReactElement {
  const previousNotes = usePreviousSoapNotes({
    appointmentId: item.id,
    previousSoapNotesScope: item.previousSoapNotesScope,
    rdId,
    getToken,
  });
  const recap = useSessionRecap({
    appointmentId: item.id,
    lane: "read-only",
    rdId,
    enabled: true,
    getToken,
  });

  return (
    <AppointmentDetailFrame
      item={item}
      listSearch={listSearch}
      timezone={timezone}
      previousNotes={previousNotes}
      recap={{ ...recap, lane: "read-only" }}
      readOnly
      renderWorkspace={(controlsEnd) => (
        <ReadOnlyAppointmentWorkspace
          key={`workspace:${item.id}:${rdId}`}
          appointmentId={item.id}
          rdId={rdId}
          note={note}
          getToken={getToken}
          controlsEnd={controlsEnd}
        />
      )}
    />
  );
}

function EditableAppointmentDetail({
  item,
  listSearch,
  timezone,
  note,
  getToken,
}: {
  item: AppointmentDetailResponse;
  listSearch: AppointmentListSearch;
  timezone: string;
  note: AppointmentNoteResponse;
  getToken: ReturnType<typeof useAuth>["getToken"];
}): ReactElement {
  useAppointmentPresenceHeartbeat({ appointmentId: item.id, getToken });
  const previousNotes = usePreviousSoapNotes({
    appointmentId: item.id,
    previousSoapNotesScope: item.previousSoapNotesScope,
    getToken,
  });
  const recap = useSessionRecap({
    appointmentId: item.id,
    lane: "rd",
    enabled: useFeatureEnabled(FEATURE_FLAGS.sessionRecap),
    getToken,
  });
  const quickNoteEnabled = useFeatureEnabled(FEATURE_FLAGS.patientQuickNote);

  return (
    <AppointmentDetailFrame
      item={item}
      listSearch={listSearch}
      timezone={timezone}
      previousNotes={previousNotes}
      recap={{ ...recap, lane: "rd" }}
      quickNote={
        quickNoteEnabled ? (
          <PatientQuickNote appointmentId={item.id} getToken={getToken} />
        ) : null
      }
      renderWorkspace={(controlsEnd) => (
        <AppointmentWorkspace
          key={`workspace-${item.id}`}
          appointmentId={item.id}
          note={note}
          getToken={getToken}
          controlsEnd={controlsEnd}
        />
      )}
    />
  );
}

type PreviousNotesController = ReturnType<typeof usePreviousSoapNotes>;
type SessionRecapBinding = SessionRecapHandle & { lane: SessionRecapLane };

function useSidePanels(recapVisible: boolean) {
  const isDesktop = useIsDesktop();
  const isWideViewport = useIsPreviousNotesWideViewport();
  // Plain React state: width is the drawer's, so it outlives the overview,
  // paging, and hide/reopen until Narrow.
  const [wide, setWide] = useState(false);
  const [requested, setRequested] = useState<OpenPanel>(() =>
    initialOpenPanel(isDesktop)
  );
  useEffect(() => setRequested(openPanelForBreakpoint(isDesktop)), [isDesktop]);
  const open = reconcileOpenPanel(requested, recapVisible);
  useShellSiderailSqueeze(shellSqueezed(isDesktop, open));

  return {
    isDesktop,
    open,
    wide,
    setWide,
    canWiden: widenAvailable(isDesktop, isWideViewport),
    previousNotesWidth: previousNotesDrawerWidth(wide, isDesktop, isWideViewport),
    set: setRequested,
    toggle: (panel: Exclude<OpenPanel, null>) =>
      setRequested((current) => toggleOpenPanel(current, panel)),
  };
}

type SidePanelsController = ReturnType<typeof useSidePanels>;

type AppointmentDetailFrameProps = {
  item: AppointmentDetailResponse;
  listSearch: AppointmentListSearch;
  timezone: string;
  previousNotes: PreviousNotesController;
  recap: SessionRecapBinding;
  readOnly?: boolean;
  /** The owning RD's Patient Quick Note; read-only lanes never pass one. */
  quickNote?: ReactNode;
  renderWorkspace: (controlsEnd: ReactElement) => ReactElement;
};

function AppointmentDetailFrame({
  item,
  listSearch,
  timezone,
  previousNotes,
  recap,
  readOnly = false,
  quickNote,
  renderWorkspace,
}: AppointmentDetailFrameProps): ReactElement {
  const [now] = useState(() => new Date().toISOString());
  const recapPanelId = useId();
  const previousNotesPanelId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const recapChip = chipView(recap.state, recap.lane);
  const panels = useSidePanels(recapChip.visible);
  const notesFocus = usePreviousNotesFocus(panels, previousNotesPanelId, toggleRef);

  return (
    <section aria-label="Appointment workspace" className="flex h-full min-h-0">
      <CanvasColumn>
        <AppointmentHeader
          item={item}
          listSearch={listSearch}
          timezone={timezone}
          readOnly={readOnly}
          chip={
            <RecapChipSlot
              view={recapChip}
              open={panels.open === "recap"}
              panelId={recapPanelId}
              onToggle={() => panels.toggle("recap")}
            />
          }
        />
        {quickNote}
        <EditorsBox>
          {renderWorkspace(
            <PreviousSoapNotesToggle
              open={panels.open === "previous-notes"}
              onToggle={notesFocus.toggle}
              panelId={previousNotesPanelId}
              buttonRef={toggleRef}
            />
          )}
        </EditorsBox>
      </CanvasColumn>
      <SidePanels
        panels={panels}
        recapPanelId={recapPanelId}
        previousNotesPanelId={previousNotesPanelId}
        closePreviousNotes={notesFocus.close}
        toggleRef={toggleRef}
        previousNotes={previousNotes}
        patientName={item.patientName}
        recap={recap}
        clock={{ now, timezone }}
      />
    </section>
  );
}

// The editors scroll inside a box that takes whatever height the header and
// Patient Quick Note leave. The floor keeps that box usable on a short window;
// past it, the canvas column scrolls.
function EditorsBox({ children }: { children: ReactNode }): ReactElement {
  return <div className="mt-[22px] flex min-h-[360px] flex-1 flex-col">{children}</div>;
}

/**
 * Focus for the Previous notes drawer: a user-initiated open (not the desktop
 * default at load) focuses its heading; the toggle keeps focus when it flips;
 * ×, Hide, and Escape in the docked drawer return focus to the toggle.
 */
function usePreviousNotesFocus(
  panels: SidePanelsController,
  panelId: string,
  toggleRef: RefObject<HTMLButtonElement | null>
) {
  const userOpened = useRef(false);
  const { set, toggle, isDesktop } = panels;
  const notesOpen = panels.open === "previous-notes";
  const close = useCallback(() => {
    set(null);
    toggleRef.current?.focus();
  }, [set, toggleRef]);

  useEffect(() => {
    if (!notesOpen || !userOpened.current || !isDesktop) {
      if (!isDesktop) userOpened.current = false;

      return;
    }

    userOpened.current = false;
    document
      .getElementById(panelId)
      ?.querySelector<HTMLElement>("[data-drawer-title]")
      ?.focus();
  }, [notesOpen, isDesktop, panelId]);

  useEffect(() => {
    if (!notesOpen || !isDesktop) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      const drawer = document.getElementById(panelId);
      if (event.key === "Escape" && drawer?.contains(event.target as Node)) close();
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [notesOpen, isDesktop, panelId, close]);

  return {
    close,
    toggle: () => {
      userOpened.current = !notesOpen;
      toggle("previous-notes");
    },
  };
}

function RecapChipSlot({
  view,
  open,
  panelId,
  onToggle,
}: {
  view: SessionRecapChipView;
  open: boolean;
  panelId: string;
  onToggle: () => void;
}): ReactElement | null {
  if (!view.visible) return null;

  return (
    <SessionRecapChip view={view} open={open} panelId={panelId} onToggle={onToggle} />
  );
}

type SidePanelsProps = {
  panels: SidePanelsController;
  recapPanelId: string;
  previousNotesPanelId: string;
  closePreviousNotes: () => void;
  toggleRef: RefObject<HTMLButtonElement | null>;
  previousNotes: PreviousNotesController;
  patientName: string | null;
  recap: SessionRecapBinding;
  clock: { now: string; timezone: string };
};

/**
 * The two mutually exclusive side panels: docked drawers at 1024px and wider,
 * full-height Sheets below. The Sheet portals render outside the flex row.
 */
function SidePanels({
  panels,
  recapPanelId,
  previousNotesPanelId,
  closePreviousNotes,
  toggleRef,
  previousNotes,
  patientName,
  recap,
  clock,
}: SidePanelsProps): ReactElement {
  const { isDesktop, open } = panels;
  const closeSheet = () => panels.set(null);
  const widen = panels.canWiden
    ? { wide: panels.wide, onToggle: () => panels.setWide(!panels.wide) }
    : undefined;
  const sheetChange = (panel: Exclude<OpenPanel, null>) => (next: boolean) =>
    panels.set(next ? panel : null);
  const recapView = panelView(recap.state, recap.lane, clock);
  const notesPanel = (docked: boolean) => (
    <PreviousNotesPanelContent
      controller={previousNotes}
      patientName={patientName}
      timezone={clock.timezone}
      now={clock.now}
      onClose={docked ? closePreviousNotes : closeSheet}
      open={open === "previous-notes"}
      widen={docked ? widen : undefined}
    />
  );

  return (
    <>
      <SidePanelPair
        drawerLabel="Previous SOAP notes drawer"
        sheetTitle="Previous SOAP notes"
        panelId={previousNotesPanelId}
        width={panels.previousNotesWidth}
        returnFocusRef={toggleRef}
        isDesktop={isDesktop}
        open={open === "previous-notes"}
        onOpenChange={sheetChange("previous-notes")}
        drawer={notesPanel(true)}
        sheet={notesPanel(false)}
      />
      {recapView ? (
        <RecapSidePanel
          view={recapView}
          panelId={recapPanelId}
          isDesktop={isDesktop}
          open={open === "recap"}
          onOpenChange={sheetChange("recap")}
          onClose={closeSheet}
          onRetry={recap.retry}
        />
      ) : null}
    </>
  );
}

function RecapSidePanel({
  view,
  panelId,
  isDesktop,
  open,
  onOpenChange,
  onClose,
  onRetry,
}: {
  view: NonNullable<ReturnType<typeof panelView>>;
  panelId: string;
  isDesktop: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
  onRetry?: (() => void) | undefined;
}): ReactElement {
  const panel = <SessionRecapPanel view={view} onClose={onClose} onRetry={onRetry} />;

  return (
    <SidePanelPair
      drawerLabel="Session recap drawer"
      sheetTitle="Session recap"
      panelId={panelId}
      isDesktop={isDesktop}
      open={open}
      onOpenChange={onOpenChange}
      drawer={panel}
      sheet={panel}
    />
  );
}

type SidePanelPairProps = {
  drawerLabel: string;
  sheetTitle: string;
  /** Carried by whichever container is live, for the chip's aria-controls. */
  panelId?: string;
  /** Docked width; the Previous notes drawer widens, the Recap stays 400. */
  width?: 400 | 640;
  /** Where focus goes when the Sheet closes. */
  returnFocusRef?: RefObject<HTMLElement | null>;
  isDesktop: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  drawer: ReactElement;
  sheet: ReactElement;
};

function SidePanelPair({
  drawerLabel,
  sheetTitle,
  panelId,
  width = 400,
  returnFocusRef,
  isDesktop,
  open,
  onOpenChange,
  drawer,
  sheet,
}: SidePanelPairProps): ReactElement {
  return (
    <>
      <AppointmentSideDrawer
        label={drawerLabel}
        open={isDesktop && open}
        width={width}
        id={isDesktop ? panelId : undefined}
      >
        {drawer}
      </AppointmentSideDrawer>
      <AppointmentSideSheet
        title={sheetTitle}
        id={panelId}
        state={previousNotesSheetState(open, isDesktop)}
        returnFocusRef={returnFocusRef}
        onOpenChange={onOpenChange}
      >
        {sheet}
      </AppointmentSideSheet>
    </>
  );
}

function PreviousNotesPanelContent({
  controller,
  patientName,
  timezone,
  now,
  onClose,
  open,
  widen,
}: {
  controller: PreviousNotesController;
  patientName: string | null;
  timezone: string;
  now: string;
  onClose: () => void;
  open: boolean;
  widen?: { wide: boolean; onToggle: () => void } | undefined;
}): ReactElement {
  return (
    <PreviousSoapNotesPanel
      view={controller.view}
      timezone={timezone}
      now={now}
      patientName={patientName}
      onSelect={controller.select}
      onRetry={controller.retry}
      onRetrySelection={controller.retrySelection}
      onClose={onClose}
      open={open}
      {...(widen ? { widen } : {})}
    />
  );
}

function AppointmentSideSheet({
  title,
  id,
  state,
  returnFocusRef,
  onOpenChange,
  children,
}: {
  title: string;
  id?: string;
  state: ReturnType<typeof previousNotesSheetState>;
  returnFocusRef?: RefObject<HTMLElement | null> | undefined;
  onOpenChange: (open: boolean) => void;
  children: ReactElement;
}): ReactElement | null {
  const contentRef = useRef<HTMLDivElement>(null);
  // The sheet opens under the shell's top bar, whichever shell is mounted.
  const geometry = useShellGeometry();
  if (!state.mounted) return null;

  // The panel renders its own close in its header, so the built-in one is
  // hidden (display: none also removes it from the tab order).
  return (
    <Sheet open={state.open} onOpenChange={onOpenChange}>
      <SheetContent
        ref={contentRef}
        id={id}
        side="right"
        onOpenAutoFocus={(event) => {
          const heading =
            contentRef.current?.querySelector<HTMLElement>("[data-drawer-title]");
          if (!heading) return;

          event.preventDefault();
          heading.focus();
        }}
        onCloseAutoFocus={(event) => {
          if (!returnFocusRef?.current) return;

          event.preventDefault();
          returnFocusRef.current.focus();
        }}
        className={cn(
          geometry.sheetFrame,
          "w-full max-w-none gap-0 border-l-0 p-0 sm:max-w-none [&>button:last-child]:hidden"
        )}
      >
        <SheetTitle className="sr-only">{title}</SheetTitle>
        {children}
      </SheetContent>
    </Sheet>
  );
}

function AppointmentSideDrawer({
  label,
  id,
  open,
  width,
  children,
}: {
  label: string;
  id?: string;
  open: boolean;
  width: 400 | 640;
  children: ReactElement;
}): ReactElement {
  const wide = width === 640;

  return (
    <aside
      id={id}
      aria-label={label}
      inert={!open}
      className={cn(
        "hidden shrink-0 overflow-hidden border-l border-border bg-card transition-[width] lg:block",
        RAIL_TRANSITION,
        open ? (wide ? "w-[640px]" : "w-[400px]") : "w-0 border-l-0"
      )}
    >
      <div className={cn("h-full", wide ? "w-[640px]" : "w-[400px]")}>{children}</div>
    </aside>
  );
}

/** The 720px canvas column, centered in whatever width the drawer leaves. */
function CanvasColumn({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 justify-center overflow-hidden">
      <div className="flex min-h-0 w-full max-w-[720px] flex-col overflow-y-auto px-5 py-5 sm:px-6 sm:py-8">
        {children}
      </div>
    </div>
  );
}

/**
 * Three facts only: patient name, appointment date and time, and whether a
 * next visit is scheduled. Type, contact type, and the status badge are gone
 * — none of them changes what the RD does on this page. Under the always
 * theme (NO-799) the Back link and breadcrumb give way to a name row (lime
 * avatar, name, a quiet "Appointments" link) over a white visit strip; the
 * classes live in workspace-look.ts.
 */
function AppointmentHeader({
  item,
  listSearch,
  timezone,
  readOnly = false,
  chip,
}: {
  item: AppointmentDetailResponse;
  listSearch: AppointmentListSearch;
  timezone: string;
  /** An Admin lane: the badge sits at the right end of the Back row. */
  readOnly?: boolean;
  chip?: ReactNode;
}): ReactElement {
  const name = item.patientName || "Patient";

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <span className={CLASSIC_BACK_SLOT}>
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="-ml-2 text-body text-muted-foreground hover:text-foreground"
          >
            <Link to="/appointments" search={listSearch}>
              <ArrowLeft aria-hidden="true" /> Back to Appointments
            </Link>
          </Button>
        </span>
        {readOnly ? <Badge variant="info">Read-only</Badge> : null}
      </div>
      <p className={CLASSIC_BREADCRUMB}>
        Appointments
        <span aria-hidden="true" className="text-sand-300">
          /
        </span>
        {shortDateLabel(item.startsAt, timezone)} ·{" "}
        {formatAppointmentTime(item.startsAt, timezone)}
      </p>
      <header className="mt-3 shrink-0">
        <div className={NAME_ROW}>
          <span aria-hidden="true" className={NAME_AVATAR}>
            {initials(name)}
          </span>
          <h1 className={NAME_TITLE}>{name}</h1>
          {chip}
          <Link to="/appointments" search={listSearch} className={HOME_LINK}>
            <span aria-hidden="true">←</span> Appointments
          </Link>
        </div>
        <VisitLine item={item} timezone={timezone} />
      </header>
    </>
  );
}
